import {
  TicketTypeSerializer as VenueBasedTicketTypeSerializer,
  VenueBasedEventSerializer,
} from "@app-contexts/dashboard/features/events/data/types/events";
import {
  DiscoverySubItemSerializer,
  PublicDiscoveryViewSerializer,
} from "@app-contexts/public/features/discovery/data/types/discovery";
import {
  CityLandingPageEventSerializer,
  CLPTicketTypeSerializer,
  PublicBaseEventSerializer,
  TicketTypeSerializer as PublicTicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import sumBy from "lodash/sumBy";
import { differenceInHours, isValid } from "date-fns";

import { enums, enumsTypes } from "@shared/constants";

import {
  FeesPricingInfoDetails,
  FeesPricingInfoPaymentTypes,
  FeesPricingInfoType,
} from "@shared/modules/financials/types";
import { SeatingEventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { Subset } from "@shared/utils/helper-types";

import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { isSameCalendarDay } from "@shared/utils/dates/comparisons/calendar-day-comparisons";
import {
  ensureDate,
  isDateAfter,
  isDateBefore,
} from "@shared/utils/dates/comparisons/comparisons";
import { formatDate } from "@shared/utils/dates/formatting";
import { TicketTypeSerializer } from "../types/public";
import {
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
  isUpcomingSaleTicketType,
} from "./ticket-type-utils";

export type InputEvent = {
  starts_on?: string;
  ends_on?: string;
  timezone?: string;
  sold_out?: boolean;
  inventory_sold_out?: PublicBaseEventSerializer["inventory_sold_out"];
  public_inventory_sold_out?: PublicBaseEventSerializer["public_inventory_sold_out"];
  is_available_for_quantity_requested?: boolean;
  virtual_event_type?: string;
  external_link?: PublicBaseEventSerializer["external_link"];
  no_ticket_types?: boolean;
  ticket_types?: TicketTypeSerializer[];
  is_waitlisted?: boolean;
  tickettype_set?: TicketTypeSerializer[];
};

/**
 * Normalizes event date strings from the API into ISO 8601–like form for date-fns/ensureDate.
 * Use when you have event date fields (e.g. starts_on, ends_on) that may be "YYYY-MM-DD HH:mm:ss"
 * or "YYYY-MM-DD H:mm:ss" (space instead of T, single-digit hour). Converts to "YYYY-MM-DDTHH:mm:ss"
 * so parseISO/ensureDate parse correctly.
 */
const normalizeEventDateString = (
  value: string | undefined
): string | undefined => {
  if (typeof value !== "string" || value.includes("T")) return value;
  const withT = value.replace(" ", "T");
  return withT.replace(/T(\d)(?=:)/, "T0$1");
};

/**
 * Returns true if the sale_ends_on field for all ticket types is in the past
 * @param event | event object
 * @returns boolean value
 */
export const isEventWithoutUpcomingTicketSales = (
  event: InputEvent
): boolean => {
  const ticketTypes = event?.ticket_types || event?.tickettype_set;
  return event && !ticketTypes?.some((tt) => isUpcomingSaleTicketType(tt));
};

/**
 * Returns true if an event is in the past and false if an event is upcoming
 * @param {*} event | event object
 * @returns boolean value
 */
export const isPastEvent = <
  T extends {
    ends_on?: InputEvent["ends_on"];
    timezone?: InputEvent["timezone"];
  }
>(
  event: T
): boolean => {
  if (!event?.ends_on) return false;
  return !isDateAfter(event.ends_on, new Date());
};

/**
 * Returns true if an event has a virtual event type equal to certain enum id values
 * @param {*} event | event object
 * @returns boolean value
 */
export const isOnlineEvent = (event: InputEvent): boolean => {
  return Boolean(
    event.virtual_event_type &&
      (event.virtual_event_type ===
        enums.VIRTUAL_EVENT_TYPE_MAP.VIRTUAL_PRIVATE.id ||
        event.virtual_event_type ===
          enums.VIRTUAL_EVENT_TYPE_MAP.VIRTUAL_PUBLIC.id)
  );
};

/**
 * Returns true if an event has at least one waitlist item. Or if event.is_waitlisted is true.
 * @param {InputEvent} event | event object
 * @returns boolean value
 */
export const eventHasWaitlistItems = (
  event?: InputEvent | SeatingEventPropertyMap
): boolean => {
  // is_waitlisted field automatically updates from ticket changes
  // if it's true, there is at least 1 waitlisted ticket
  if (event?.is_waitlisted) {
    return true;
  }

  // Check for atleast 1 ticket_type with waitlist attached
  if (!event || !event.ticket_types || event.ticket_types.length === 0) {
    return false;
  }

  const hasWaitlistTTs = event.ticket_types.some((tt) =>
    isTicketTypeWaitlisted(tt)
  );

  return hasWaitlistTTs;
};

/**
 * Returns true if an event has `sold_out`, `inventory_sold_out` or `public_inventory_sold_out` set to true
 *
 * if event has external link or `no_ticket_types` = true, returns false
 * if `is_available_for_quantity_requested` is explicitly false, returns true
 * @param {*} event | event object
 * @returns boolean value
 */
export const isSoldOutEvent = (
  event?: InputEvent | SeatingEventPropertyMap
): boolean => {
  if (event?.external_link || event?.no_ticket_types) {
    return false;
  }

  return Boolean(
    event?.sold_out ||
      event?.inventory_sold_out ||
      event?.public_inventory_sold_out
  );
};

/**
 * Returns true if an event is sold out AND has no waitlist fallback.
 * Intended for calendar/discovery surfaces that treat waitlisted events as
 * still actionable. For detail-page button text, combine `isSoldOutEvent`
 * with `eventHasWaitlistItems` at the call site.
 */
export const isSoldOutAndUnwaitlistedEvent = (
  event?: InputEvent | SeatingEventPropertyMap
): boolean => {
  return isSoldOutEvent(event) && !eventHasWaitlistItems(event);
};

/**
 * Returns true if an event has `is_available_for_quantity_requested` set to true
 *
 * Tightly coupled with calendar logic, hence the absence of a quantity being passed into this function.
 * This function is used after an event has been fetched from a calendar endpoint request that contains
 * the quantity being requested in the request payload.
 *
 * @param {*} event | event object
 * @returns boolean value
 */
export const isEventAvailableForQuantityRequested = <T>(
  event: T & {
    is_available_for_quantity_requested?: boolean;
  }
) => {
  // When this is nullish, it could mean that this utility function is being used against an object coming from a serializer
  // that does not support the calculated field; we can assume that the event is available.
  if (event.is_available_for_quantity_requested == null) {
    return true;
  }

  return Boolean(event.is_available_for_quantity_requested);
};

/**
 * Returns true if an event has `sold_out` or `inventory_sold_out` set to true
 *
 * does not check for public_inventory_sold_out
 * @param {*} event | event object
 * @returns boolean value
 */
export const isBoxOfficeEventSoldOut = (
  event: VenueBasedEventSerializer
): boolean => {
  return Boolean(event?.sold_out || event?.inventory_sold_out);
};

export const boxOfficeEventUnavailableForExchange = (
  event: VenueBasedEventSerializer
): boolean => {
  const exchangeCredit = isStorageAvailable("localStorage")
    ? localStorage.getItem("exchangeCreditData")
    : null;
  const exchangeCreditData = JSON.parse(exchangeCredit || "{}");

  return Boolean(
    exchangeCreditData?.is_membership_ticket_exchange &&
      event?.membership_ticket_types?.length === 0
  );
};

export interface IHasStartAndEnd {
  starts_on: string;
  ends_on: string;
  timezone?: string;
}

export interface IHasTBD {
  date_time_to_be_determined?: boolean;
}

export type IHasPrintableDate = IHasStartAndEnd | IHasTBD;

export const isIHasStartAndEnd = (obj: object): obj is IHasStartAndEnd => {
  return obj && "starts_on" in obj && "ends_on" in obj;
};

/**
 *
 * @param ticketType ticket type to check
 * @param event event object
 * @returns whether the event is sold out or if the ticket type is sold out. Used to check if an event has reached the
 * maximum event capacity, but individual ticket types may not be sold out. In this case, ticket types should be treated
 * as sold out.
 */
export const isTicketOrEventSoldOut = (
  ticketType: PublicTicketTypeSerializer,
  event: InputEvent
): boolean => {
  return isSoldOutEvent(event) || isTicketTypeSoldOut(ticketType);
};

/**
 * Returns true if an event spans multiple days
 * @param event event object
 * @return {boolean}
 */
export const isMultiDayEvent = (obj: IHasPrintableDate): boolean => {
  if (!isIHasStartAndEnd(obj)) {
    return false;
  }

  const { starts_on, ends_on, timezone } = obj;
  const start = ensureDate(normalizeEventDateString(starts_on));
  const end = ensureDate(normalizeEventDateString(ends_on));
  if (!isValid(start) || !isValid(end)) return false;

  const durationHours = differenceInHours(end, start);

  if (durationHours >= 24) {
    return true;
  }

  // event less than 24 hours but spans two days
  // consider durations greater than 12 hours as multi-day
  if (durationHours >= 12) {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startDay = formatDate({
      date: start,
      timezone: tz,
      format: "yyyy/MM/dd",
    });
    const endDay = formatDate({
      date: end,
      timezone: tz,
      format: "yyyy/MM/dd",
    });
    return startDay !== endDay;
  }

  return false;
};

/**
 * Returns a formatted string of regions
 * @param allowed_regions array of strings representing regions (example: ["BC", "AB"])
 * @return {string}
 */
export const getAllowedRegions = (allowed_regions: string[]): string => {
  if (!allowed_regions) {
    return "";
  }

  // format the string so that we have a correct output
  // example "AB, BC" ...

  return allowed_regions.reduce((acc, curr, idx) => {
    if (idx > 0) {
      return `${acc}, ${curr}`;
    }
    return `${curr}`;
  }, "");
};

/**
 * Get an event's remaining inventory if it is within the limits of the tt's
 * @param ticketTypes TicketTypeSerializer array
 */
export const getTotalInventoryLeft = (
  ticketTypes?: Array<PublicTicketTypeSerializer>
): number => {
  // Some cases the calendar may call this without any ticket types. In that case just return.
  if (!ticketTypes) {
    return 0;
  }

  if (ticketTypes.every((tt) => tt.inventory_left)) {
    return sumBy(ticketTypes, "inventory_left");
  }

  return 0;
};

type InventoryStatusType = {
  displayValue: boolean;
  inventoryValue: number | null;
};

/**
 * Displays or hides the LowInventoryStatus/RemainingTicketStatus components, based on the return value
 * Also gets the remaining amount of ticket inventory
 * @param event event object
 * @return {InventoryStatusType}
 */
export const eventDisplayLowInventory = (
  event:
    | PublicBaseEventSerializer
    | VenueBasedEventSerializer
    | SeatingEventPropertyMap
): InventoryStatusType => {
  let showRemainingTickets = false;

  const ticketTypes = "ticket_types" in event ? event.ticket_types : undefined;
  const ticketTypeSet =
    "tickettype_set" in event ? event.tickettype_set : undefined;

  // get remaining inventory amount
  let totalAmountLeft: number | null = getTotalInventoryLeft(
    ticketTypes ?? ticketTypeSet ?? []
  );

  // Clear total amount left if it is not within the limit for the event.
  if (
    totalAmountLeft &&
    event?.show_inventory_amount &&
    event?.show_inventory_amount < totalAmountLeft
  ) {
    totalAmountLeft = null;
  } else if (
    totalAmountLeft &&
    event?.venue?.show_inventory_amount &&
    event?.venue?.show_inventory_amount < totalAmountLeft
  ) {
    totalAmountLeft = null;
  }
  // Boolean determines whether to show the LowInventoryStatus or RemainingTicketStatus component. Don't show if there
  // is event inventory as it is hard to correctly calculate based on what the back end provides.
  // Ticketype amount left should still be shown though.
  if (event.inventory) {
    totalAmountLeft = null;
  }
  showRemainingTickets = Boolean(totalAmountLeft);

  const returnValue = {
    displayValue: showRemainingTickets,
    inventoryValue: totalAmountLeft,
  };

  return returnValue;
};

const getStartDateFormat = (firstYear: number, secondYear: number): string => {
  // default: do not display the year for the first date in the range
  let returnFormat = "MMM D";

  // if the event starts and ends in different years,
  // display the year on the event start date
  if (firstYear !== secondYear) {
    returnFormat = "MMM D YYYY";
  }

  return returnFormat;
};

const getEndDateFormat = (
  firstMonth: number,
  firstYear: number,
  secondMonth: number,
  secondYear: number
): string => {
  // default: display the month and year for the second date in the range
  let returnFormat = "MMM D YYYY";

  // if the event starts and ends in the same month/year,
  // omit the month on the end date
  if (firstMonth === secondMonth && firstYear === secondYear) {
    returnFormat = "D YYYY";
  }

  return returnFormat;
};

export const getStaticEventDateRange = (
  event: IHasStartAndEnd | IHasTBD,
  tbdText: string
): string => {
  const singleDateFormat = "dddd MMMM Do, YYYY";

  // display TBD text if event is TBD
  if (!isIHasStartAndEnd(event)) {
    return tbdText;
  }

  const tz =
    event?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const startDate = ensureDate(event.starts_on);
  const endDate = ensureDate(event.ends_on);

  if (!isMultiDayEvent(event)) {
    return formatDate({
      date: startDate,
      timezone: tz,
      format: singleDateFormat,
    });
  }

  const startYear = formatDate({
    date: startDate,
    timezone: tz,
    format: "YYYY",
  });
  const endYear = formatDate({
    date: endDate,
    timezone: tz,
    format: "YYYY",
  });
  const startDateFormat =
    startYear !== endYear ? "ddd MMM Do, YYYY" : "ddd MMM Do";
  const endDateFormat = "ddd MMM Do, YYYY";

  return `${formatDate({
    date: startDate,
    timezone: tz,
    format: startDateFormat,
  })} - ${formatDate({
    date: endDate,
    timezone: tz,
    format: endDateFormat,
  })}`;
};

/**
 * This function returns a dynamic, formatted date range for multiday events and a single formatted date for single-day events
 * If the multiday event is upcoming or ended, then the event's actual start and end dates are returned
 * If the multiday event has already started and not ended, then the current date/time and the event's end date are returned
 * If the multiday event ends on the current date, then only the current date is returned
 * @param {Object} event | event object
 * @param {string} tbdText | translated text displayed when event date/time is TBD
 * @returns string value that contains the event's formatted date
 */
export const getDynamicEventDateRange = (
  event:
    | PublicBaseEventSerializer
    | CityLandingPageEventSerializer
    | PublicDiscoveryViewSerializer,
  tbdText: string
): string => {
  const tz =
    event?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // default value for single-day events is the event's start time
  let dateText = formatDate({
    date: event?.starts_on ?? new Date(NaN),
    timezone: tz,
    format: "MMM D yyyy",
  });

  // display TBD text if event is TBD
  if (event?.date_time_to_be_determined) {
    dateText = tbdText;
  }
  // check if the event is over 24 hours long
  else if (
    !event?.date_time_to_be_determined &&
    isMultiDayEvent(event as IHasPrintableDate)
  ) {
    const now = new Date();
    const startDate = ensureDate(event.starts_on);
    const endDate = ensureDate(event.ends_on);

    // variables for the event's start date and end date (month/year in event timezone)
    const currentMonth = parseInt(
      formatDate({ date: now, timezone: tz, format: "M" }),
      10
    );
    const currentYear = parseInt(
      formatDate({ date: now, timezone: tz, format: "yyyy" }),
      10
    );
    const startMonth = parseInt(
      formatDate({ date: startDate, timezone: tz, format: "M" }),
      10
    );
    const startYear = parseInt(
      formatDate({ date: startDate, timezone: tz, format: "yyyy" }),
      10
    );
    const endMonth = parseInt(
      formatDate({ date: endDate, timezone: tz, format: "M" }),
      10
    );
    const endYear = parseInt(
      formatDate({ date: endDate, timezone: tz, format: "yyyy" }),
      10
    );

    // if the event is upcoming or has already ended,
    // display the event's start date as the first date in the range
    if (isDateBefore(now, startDate) || isDateAfter(now, endDate)) {
      const firstDateFormat = getStartDateFormat(startYear, endYear);
      const secondDateFormat = getEndDateFormat(
        startMonth,
        startYear,
        endMonth,
        endYear
      );
      dateText = `${formatDate({
        date: startDate,
        timezone: tz,
        format: firstDateFormat,
      })} - ${formatDate({
        date: endDate,
        timezone: tz,
        format: secondDateFormat,
      })}`;
    }
    // if the event has already started and hasn't yet ended,
    // display the current date as the first date in the range
    else {
      const firstDateFormat = getStartDateFormat(currentYear, endYear);
      const secondDateFormat = getEndDateFormat(
        currentMonth,
        currentYear,
        endMonth,
        endYear
      );

      // do not display a date range on the last day of a multiday event
      if (isSameCalendarDay(now, endDate, tz)) {
        dateText = formatDate({
          date: now,
          timezone: tz,
          format: "MMM D yyyy",
        });
      }
      // display a date range when the event has already started and will end in the future
      else {
        dateText = `${formatDate({
          date: now,
          timezone: tz,
          format: firstDateFormat,
        })} - ${formatDate({
          date: endDate,
          timezone: tz,
          format: secondDateFormat,
        })}`;
      }
    }
  }

  return dateText;
};

export const getEventDateTimeString = (
  event: PublicBaseEventSerializer,
  tbdString: string
): string => {
  // Handle TBD events
  if (event?.date_time_to_be_determined) {
    return tbdString;
  }

  // For single-day events, return full date-time string with timezone
  if (!isMultiDayEvent(event)) {
    return formatDate({
      date: event.starts_on,
      timezone: event.timezone,
      format: "EEE, MMM d yyyy",
      displayTime: true,
    });
  }

  // For multi-day events, return the dynamic date range
  return getDynamicEventDateRange(event, tbdString);
};

export const getStartsOnDate = (
  event:
    | Required<Pick<PublicBaseEventSerializer, "starts_on">>
    | Required<Pick<VenueBasedEventSerializer, "starts_on">>,
  ticketType?:
    | Pick<VenueBasedTicketTypeSerializer, "custom_ticket_event_starts_on">
    | Pick<PublicTicketTypeSerializer, "custom_ticket_event_starts_on">
) => {
  if (ticketType?.custom_ticket_event_starts_on) {
    return ticketType.custom_ticket_event_starts_on;
  }
  return event.starts_on;
};

export const getEventId = (
  event:
    | PublicDiscoveryViewSerializer
    | PublicBaseEventSerializer
    | CityLandingPageEventSerializer
): number | null => {
  const eventItemType = enums.DISCOVERY_VIEW_TYPE_MAP.event.id;
  let eventId: number | null = null;
  if ("id" in event) {
    eventId = event.id;
  } else if (
    "item_id" in event &&
    // Only provide event id if the discovery item is an event. If not we will return Null.
    event.type === eventItemType
  ) {
    eventId = event.item_id;
  }
  return eventId;
};

const omitUndefinedFields = <T>(obj: T): T => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key as keyof T];
    if (value !== undefined) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as T);
};

/**
 * Converts a CityLandingPageEventSerializer object to a PublicDiscoveryViewSerializer object.
 *
 * Sample Input (CityLandingPageEventSerializer):
 * {
 *   id: 1,
 *   name: "Sample Event",
 *   starts_on: "2023-06-10T10:00:00Z",
 *   ends_on: "2023-06-10T18:00:00Z",
 *   ticket_types: [
 *     {
 *       id: 1,
 *       name: "General Admission",
 *       price: 50,
 *       configured_fees_pricing_info: {
 *         psp_web: {
 *           1: {
 *             total_price_no_tax: 5,
 *           },
 *         },
 *       },
 *     },
 *   ],
 *   venue: {
 *     id: 1,
 *     name: "Sample Venue",
 *   },
 * }
 *
 * Sample Output (PublicDiscoveryViewSerializer):
 * {
 *   uuid: "1",
 *   type: 'event',
 *   item_id: 1,
 *   name: "Sample Event",
 *   starts_on: "2023-06-10T10:00:00Z",
 *   ends_on: "2023-06-10T18:00:00Z",
 *   sub_items: [
 *     {
 *       id: 1,
 *       name: "General Admission",
 *       price: 50,
 *       fees_pricing_info: {
 *         psp_web: {
 *           1: {
 *             total_price_no_tax: '5',
 *             service_charges: '',
 *             taxes: '',
 *             total_price: '',
 *             sum_custom_fees: [],
 *             sum_internal_fees: '',
 *           },
 *         },
 *       },
 *     },
 *   ],
 *   venue: {
 *     id: 1,
 *     name: "Sample Venue",
 *   },
 * }
 */
export const convertToPublicDiscoveryViewSerializer = (
  event: CityLandingPageEventSerializer
): PublicDiscoveryViewSerializer => {
  // Map ticket types to sub_items using array iterations
  const subItems: DiscoverySubItemSerializer[] = (event.ticket_types ?? []).map(
    (ticket: CLPTicketTypeSerializer) => {
      const feesPricingInfo: FeesPricingInfoType = {};

      // Flatten the configured_fees_pricing_info structure using reduce
      Object.entries(ticket.configured_fees_pricing_info ?? {}).forEach(
        ([paymentType, feeTypes]) => {
          feesPricingInfo[paymentType] = Object.entries(feeTypes).reduce(
            (acc, [feeType, feeDetails]) => {
              acc[feeType] = {
                service_charges: "",
                taxes: "",
                total_price: "",
                total_price_no_tax: feeDetails.total_price_no_tax,
                sum_custom_fees: [],
                sum_internal_fees: "",
              };
              return acc;
            },
            {} as FeesPricingInfoPaymentTypes
          );
        }
      );

      return omitUndefinedFields({
        id: ticket.id,
        name: ticket.name,
        price: ticket.price,
        inventory_left: ticket.inventory_left,
        fees_pricing_info: feesPricingInfo,
      });
    }
  );

  return omitUndefinedFields({
    uuid: event.id?.toString(), // No uuid so we will just use the string rep of id in these conversion cases.
    item_id: event.id,
    type: enums.DISCOVERY_VIEW_TYPE_MAP.event.id,
    venue: event.venue
      ? omitUndefinedFields({
          id: event.venue.id,
          name: event.venue.name,
          slug: event.venue.slug,
          web_address: event.venue.web_address,
          currency: event.venue.currency,
          all_in_pricing_is_enabled: event.venue.all_in_pricing_is_enabled,
          low_inventory_threshold_low: event.venue.low_inventory_threshold_low,
          low_inventory_threshold_middle:
            event.venue.low_inventory_threshold_middle,
          low_inventory_threshold_high:
            event.venue.low_inventory_threshold_high,
          low_inventory_warning_enabled:
            event.venue.low_inventory_warning_enabled,
        })
      : undefined,
    venue_id: event.venue?.id,
    name: event.name,
    description_without_html: event.description_without_html,
    starts_on: event.starts_on,
    ends_on: event.ends_on,
    date_time_to_be_determined: event.date_time_to_be_determined,
    timezone: event.timezone,
    is_recurring: event.is_recurring,
    is_recurring_parent: event.is_recurring_parent,
    child_count: event.child_count,
    external_link: event.external_link,
    frontend_details_url: event.frontend_details_url,
    image: event.image,
    image_banner: event.image_banner,
    low_inventory_warning_enabled: event.low_inventory_warning_enabled,
    low_ticket_inventory: event.low_ticket_inventory,
    thumbnail: event.thumbnail,
    inventory: event.inventory,
    inventory_sold_out: event.inventory_sold_out,
    public_inventory_sold_out: undefined, // Not included in city serializer
    no_sub_items: event.no_ticket_types,
    opens_at: event.opens_at || null,
    slug: event.slug,
    sold_out: event.sold_out,
    status: event.status,
    virtual_type: event.virtual_event_type,
    categories: [], // Not included in city serializer
    tags: [], // Not included in city serializer
    sub_items: subItems,
    location: event.location
      ? omitUndefinedFields({
          street_name: event.location.street_name,
          city: event.location.city,
          province: event.location.province,
          postal_code: event.location.postal_code,
          name: event.location.name,
          country: event.location.country,
        })
      : undefined,
    point_location: event.location?.position,
    is_sponsored: false,
    ranking_factor: undefined,
    renewal_frequency: undefined, // Add logic if applicable
    average_rating: event.average_rating,
    total_ratings: event.total_ratings,
    ratings_distribution: event.ratings_distribution,
  });
};

/**
 * Converts a PublicDiscoveryViewSerializer object to a CityLandingPageEventSerializer object.
 *
 * Sample Input (PublicDiscoveryViewSerializer):
 * {
 *   uuid: "1",
 *   type: 'event',
 *   item_id: 1,
 *   name: "Sample Event",
 *   starts_on: "2023-06-10T10:00:00Z",
 *   ends_on: "2023-06-10T18:00:00Z",
 *   sub_items: [
 *     {
 *       id: 1,
 *       name: "General Admission",
 *       price: 50,
 *       fees_pricing_info: {
 *         psp_web: {
 *           1: {
 *             total_price_no_tax: '5',
 *             service_charges: '1',
 *             taxes: '1',
 *             total_price: '3',
 *             sum_custom_fees: [{ name: "Facility Fee", fee_total: "1.00"}],
 *             sum_internal_fees: '1',
 *           },
 *         },
 *       },
 *     },
 *   ],
 *   venue: {
 *     id: 1,
 *     name: "Sample Venue",
 *   },
 * }
 *
 * Sample Output (CityLandingPageEventSerializer):
 * {
 *   id: 1,
 *   name: "Sample Event",
 *   starts_on: "2023-06-10T10:00:00Z",
 *   ends_on: "2023-06-10T18:00:00Z",
 *   ticket_types: [
 *     {
 *       id: 1,
 *       name: "General Admission",
 *       price: 50,
 *       configured_fees_pricing_info: {
 *         psp_web: {
 *           1: {
 *             total_price_no_tax: '5',
 *           },
 *         },
 *       },
 *     },
 *   ],
 *   venue: {
 *     id: 1,
 *     name: "Sample Venue",
 *   },
 * }
 */
export const convertToCityLandingEventSerializer = (
  discoveryView: PublicDiscoveryViewSerializer
): CityLandingPageEventSerializer => {
  // Map the sub_items to ticket_types
  const ticketTypes: CLPTicketTypeSerializer[] = (
    discoveryView.sub_items ?? []
  ).map((subItem: DiscoverySubItemSerializer) => {
    const configuredFeesPricingInfo: {
      [key: string]: {
        [key: string]: Subset<
          FeesPricingInfoDetails,
          {
            total_price_no_tax: string;
          }
        >;
      };
    } = {};

    Object.entries(subItem.fees_pricing_info ?? {}).forEach(
      ([paymentType, feeTypes]) => {
        configuredFeesPricingInfo[paymentType] = Object.entries(
          feeTypes
        ).reduce(
          (acc, [feeType, feeDetails]) => {
            acc[feeType] = {
              total_price_no_tax: feeDetails.total_price_no_tax,
            };
            return acc;
          },
          {} as {
            [key: string]: Subset<
              FeesPricingInfoDetails,
              {
                total_price_no_tax: string;
              }
            >;
          }
        );
      }
    );

    return omitUndefinedFields({
      id: subItem.id,
      name: subItem.name,
      price: subItem.price,
      inventory_left: subItem.inventory_left,
      configured_fees_pricing_info: configuredFeesPricingInfo,
      sale_starts_on: "",
      sold_out: false,
      sale_ends_on: "",
    });
  });

  return omitUndefinedFields({
    // Map fields from PublicDiscoveryViewSerializer to CityLandingPageEventSerializer
    id: discoveryView.item_id,
    name: discoveryView.name,
    description_without_html: discoveryView.description_without_html,
    starts_on: discoveryView.starts_on,
    ends_on: discoveryView.ends_on,
    date_time_to_be_determined: discoveryView.date_time_to_be_determined,
    timezone: discoveryView.timezone,
    is_recurring: discoveryView.is_recurring,
    is_recurring_parent: discoveryView.is_recurring_parent,
    child_count: discoveryView.child_count,
    external_link: discoveryView.external_link,
    frontend_details_url: discoveryView.frontend_details_url,
    image: discoveryView.image,
    image_banner: discoveryView.image_banner,
    low_inventory_warning_enabled: discoveryView.low_inventory_warning_enabled,
    low_ticket_inventory: discoveryView.low_ticket_inventory,
    thumbnail: discoveryView.thumbnail,
    inventory: discoveryView.inventory,
    inventory_sold_out: discoveryView.inventory_sold_out,
    no_ticket_types: discoveryView.no_sub_items,
    opens_at: discoveryView.opens_at,
    slug: discoveryView.slug,
    sold_out: discoveryView.sold_out,
    status: discoveryView.status as unknown as enumsTypes.EventStatusIdType,
    virtual_event_type: discoveryView.virtual_type,
    location: discoveryView.location
      ? omitUndefinedFields({
          city: discoveryView.location.city,
          country: discoveryView.location.country,
          name: discoveryView.location.name,
          province: discoveryView.location.province,
          position: discoveryView.point_location,
          street_name: discoveryView.location.street_name,
          postal_code: discoveryView.location.postal_code,
        })
      : undefined,
    venue: discoveryView.venue
      ? omitUndefinedFields({
          id: discoveryView.venue.id,
          name: discoveryView.venue.name,
          slug: discoveryView.venue.slug,
          web_address: discoveryView.venue.web_address,
          currency: discoveryView.venue.currency,
          all_in_pricing_is_enabled:
            discoveryView.venue.all_in_pricing_is_enabled,
          low_inventory_threshold_low:
            discoveryView.venue.low_inventory_threshold_low,
          low_inventory_threshold_middle:
            discoveryView.venue.low_inventory_threshold_middle,
          low_inventory_threshold_high:
            discoveryView.venue.low_inventory_threshold_high,
          low_inventory_warning_enabled:
            discoveryView.venue.low_inventory_warning_enabled,
        })
      : undefined,
    ticket_types: ticketTypes,
    artists: discoveryView.artists,
  });
};

export const isVenueBasedEvent = (
  event: PublicBaseEventSerializer | VenueBasedEventSerializer
): event is VenueBasedEventSerializer => {
  return event && "tickettype_set" in event;
};

export const isPublicEvent = (
  event: PublicBaseEventSerializer | VenueBasedEventSerializer
): event is PublicBaseEventSerializer => {
  return event && "ticket_types" in event;
};
